import {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ChevronDownIcon } from '../../../assets/icons';
import { useIsDesktop } from '../../hooks';
import { tokens } from '../../theme';
import type { FormFieldState } from '../FormField/FormField';

export type SelectFieldOption = { id: string; label: string };

const MOBILE_HEIGHT = 40;
const DESKTOP_HEIGHT = 48;
const MOBILE_ICON_SIZE = 20;
const DESKTOP_ICON_SIZE = 24;
const MOBILE_LABEL_GAP = 4;
const DESKTOP_LABEL_GAP = 8;
const MOBILE_PADDING = '8px 36px 8px 12px';
const DESKTOP_PADDING = '12px 44px 12px 16px';
const MOBILE_ICON_OFFSET = 8;
const DESKTOP_ICON_OFFSET = 16;
const OPTION_HEIGHT = 32;
const MAX_VISIBLE_OPTIONS = 6;

const BORDER_COLOR_BY_STATE: Record<FormFieldState, string> = {
  default: tokens.colors.brand.green.dark50,
  valid: tokens.colors.brand.green.dark50,
  error: tokens.colors.brand.red.DEFAULT,
};

type SelectFieldProps = {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  options: SelectFieldOption[];
  state: FormFieldState;
  onChange: (value: string) => void;
  onBlur: () => void;
  error?: string;
  loading?: boolean;
};

export const SelectField = ({
  id,
  label,
  placeholder,
  value,
  options,
  state,
  onChange,
  onBlur,
  error,
  loading = false,
}: SelectFieldProps): ReactElement => {
  const isDesktop = useIsDesktop();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const iconSize = isDesktop ? DESKTOP_ICON_SIZE : MOBILE_ICON_SIZE;
  const helperText = state === 'error' ? error : undefined;
  const helperId = helperText ? `${id}-helper` : undefined;
  const listboxId = `${id}-listbox`;
  const selectedIndex = options.findIndex((option) => option.id === value);
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const displayText = loading ? 'Carregando...' : (selectedOption?.label ?? placeholder);

  const closeList = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        closeList();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen, closeList]);

  const openList = useCallback(() => {
    if (loading || options.length === 0) {
      return;
    }
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setIsOpen(true);
  }, [loading, options.length, selectedIndex]);

  const selectOption = useCallback(
    (option: SelectFieldOption) => {
      onChange(option.id);
      closeList();
      buttonRef.current?.focus();
    },
    [onChange, closeList],
  );

  const moveHighlight = useCallback(
    (direction: 1 | -1) => {
      setHighlightedIndex((previous) => {
        if (options.length === 0) {
          return -1;
        }
        const base = previous < 0 ? 0 : previous;
        return (base + direction + options.length) % options.length;
      });
    },
    [options.length],
  );

  const handleTriggerClick = () => {
    if (isOpen) {
      closeList();
    } else {
      openList();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (loading) {
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (isOpen) {
          moveHighlight(1);
        } else {
          openList();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          moveHighlight(-1);
        } else {
          openList();
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          selectOption(options[highlightedIndex]);
        } else {
          openList();
        }
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          closeList();
        }
        break;
      default:
        break;
    }
  };

  const handleBlur = (_event: FocusEvent<HTMLButtonElement>) => {
    closeList();
    onBlur();
  };

  const preventTriggerBlur = (event: MouseEvent<HTMLLIElement>) => {
    // Evita que o clique na opção tire o foco do botão antes do onClick disparar.
    event.preventDefault();
  };

  return (
    <div
      ref={wrapperRef}
      style={{ ...styles.field, gap: isDesktop ? DESKTOP_LABEL_GAP : MOBILE_LABEL_GAP }}
    >
      <label htmlFor={id} style={styles.label}>
        {label}
      </label>
      <div style={styles.inputWrapper}>
        <button
          ref={buttonRef}
          id={id}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-invalid={state === 'error'}
          aria-describedby={helperId}
          aria-activedescendant={
            isOpen && highlightedIndex >= 0
              ? `${id}-option-${options[highlightedIndex].id}`
              : undefined
          }
          disabled={loading}
          onClick={handleTriggerClick}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          style={{
            ...styles.trigger,
            height: isDesktop ? DESKTOP_HEIGHT : MOBILE_HEIGHT,
            padding: isDesktop ? DESKTOP_PADDING : MOBILE_PADDING,
            borderColor: BORDER_COLOR_BY_STATE[state],
          }}
        >
          <span style={styles.triggerText}>{displayText}</span>
        </button>
        <ChevronDownIcon
          width={iconSize}
          height={iconSize}
          aria-hidden="true"
          focusable="false"
          style={{
            ...styles.icon,
            right: isDesktop ? DESKTOP_ICON_OFFSET : MOBILE_ICON_OFFSET,
            transform: `translateY(-50%) rotate(${isOpen ? 180 : 0}deg)`,
          }}
        />
        {isOpen && options.length > 0 && (
          <ul id={listboxId} role="listbox" style={styles.listbox}>
            {options.map((option, index) => (
              <li
                key={option.id}
                id={`${id}-option-${option.id}`}
                role="option"
                aria-selected={option.id === value}
                onMouseDown={preventTriggerBlur}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectOption(option)}
                style={{
                  ...styles.option,
                  backgroundColor:
                    index === highlightedIndex
                      ? tokens.colors.surface.page
                      : tokens.colors.global.white,
                  fontWeight:
                    option.id === value
                      ? tokens.typography.fontWeight.medium
                      : tokens.typography.fontWeight.regular,
                }}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
      {helperText && (
        <p
          id={helperId}
          role="alert"
          style={{ ...styles.helper, color: tokens.colors.brand.red.DEFAULT }}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  field: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box',
  },
  label: {
    fontSize: tokens.typography.fontSize.body,
    fontWeight: tokens.typography.fontWeight.regular,
    color: tokens.colors.brand.gray.dark150,
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: tokens.radius.small,
    border: '1px solid',
    backgroundColor: tokens.colors.global.white,
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
  },
  triggerText: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.typography.fontSize.input,
    fontWeight: tokens.typography.fontWeight.medium,
    color: tokens.colors.brand.gray.light150,
  },
  icon: {
    position: 'absolute',
    top: '50%',
    pointerEvents: 'none',
  },
  listbox: {
    listStyle: 'none',
    margin: 0,
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    maxHeight: OPTION_HEIGHT * MAX_VISIBLE_OPTIONS,
    overflowY: 'auto',
    backgroundColor: tokens.colors.global.white,
    border: `1px solid ${tokens.colors.border.field}`,
    borderRadius: tokens.radius.small,
    padding: tokens.spacing.none,
    zIndex: tokens.zIndex.dropdown,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    height: OPTION_HEIGHT,
    boxSizing: 'border-box',
    padding: '8px 8px 8px 12px',
    fontSize: tokens.typography.fontSize.dropdownOption,
    color: tokens.colors.brand.gray.light150,
    cursor: 'pointer',
  },
  helper: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.disclaimer,
    textAlign: 'left',
  },
};
